import { useClerk } from '@clerk/clerk-react';
import { useEffect, useRef, useState } from 'react';
import { capture } from '@tagadapay/analytics';

import { useCrmV3Banner } from '@/lib/api/crm-v3';
import { CrmV3WaitlistBannerView } from './crm-v3-waitlist-banner-view';

const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const snoozeKey = (orgSlug: string) => `tgd_crm_v3_banner_snoozed_at:${orgSlug}`;

function isSnoozed(orgSlug: string): boolean {
  try {
    const at = Number(window.localStorage.getItem(snoozeKey(orgSlug)));
    return Number.isFinite(at) && at > 0 && Date.now() - at < SNOOZE_MS;
  } catch {
    return false;
  }
}

function snooze(orgSlug: string): void {
  try {
    window.localStorage.setItem(snoozeKey(orgSlug), String(Date.now()));
  } catch {
    // Storage blocked: the banner comes back on the next load.
  }
}

type Props = { orgSlug: string };

/** CRM V3 invitation for V2 merchants: open CRM V3, join its waitlist, or see the waitlist status. */
export function CrmV3WaitlistBanner({ orgSlug }: Props) {
  const clerk = useClerk();
  const [dismissedOrg, setDismissedOrg] = useState<string | null>(null);
  const isClosed = dismissedOrg === orgSlug || isSnoozed(orgSlug);
  const { state, href } = useCrmV3Banner(orgSlug, { enabled: !isClosed });
  const shown = !isClosed && state !== 'hidden' && href !== null ? state : null;
  const viewedKey = useRef<string | null>(null);

  // Analytics only: one view per org and state.
  useEffect(() => {
    if (shown === null) return;
    const key = `${orgSlug}:${shown}`;
    if (viewedKey.current === key) return;
    viewedKey.current = key;
    capture('access:v3_banner_view', { state: shown });
  }, [orgSlug, shown]);

  if (shown === null || href === null) return null;

  const handleOpen = () => capture('access:v3_banner_click', { state: shown });
  const handleDismiss = () => {
    snooze(orgSlug);
    setDismissedOrg(orgSlug);
    capture('access:v3_banner_close', { state: shown });
  };

  // A Clerk development instance (staging) keeps one session per host: the URL carries it to CRM V3.
  // In production both hosts share the session and the URL comes back unchanged.
  return (
    <CrmV3WaitlistBannerView
      state={shown}
      href={clerk.buildUrlWithAuth(href)}
      onOpen={handleOpen}
      onDismiss={handleDismiss}
    />
  );
}
